/**
 * DEFCAT P6 — TanStack Query hooks for the taxonomy/v1 admin WRITE surface
 * (the Defect Catalogue editor). 14 endpoints: catalogue lifecycle, version
 * lifecycle (list / branch / publish / diff), entry CRUD on drafts, and the
 * validity / axis-levels / ai-map overlays.
 *
 * Wiring note — the host's `useApi()` axios instance is BASE-PINNED to
 * `/api/lyse/v1` (the analyst surface). The taxonomy admin endpoints live in
 * their own gateway namespace (`/api/taxonomy/v1/...`). Rather than mint a
 * second axios instance (and lose the shared auth / tenant / refresh
 * interceptors), every request below passes a per-request `baseURL` override
 * (`getTaxonomyBaseUrl()`) so axios resolves the relative path against the
 * taxonomy base instead of the lyse base. The interceptors (Authorization,
 * X-Tenant-ID, refresh-on-401) still apply because it's the same instance.
 *
 * The catalogue LIST itself reuses the existing lyse `GET /defect-catalogues`
 * (see `useAdminCataloguesQuery`) — taxonomy/v1 has no list endpoint; it only
 * owns the write surface.
 *
 * Conventions mirror `collectionsQueries.ts` (staleTime 5m / gcTime 10m /
 * retry 1 / meta.failureToast). Mutations invalidate the affected query keys.
 *
 * SYNC: backend-requirement taxonomy-1.0.0-defcat-admin.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getTaxonomyBaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import type {
  CatalogueExport,
  CatalogueDiff,
  CatalogueVersionSummary,
  CreateCatalogueBody,
  CreateEntryBody,
  DefectCatalogueAdmin,
  DefectCatalogueEntryV2,
  GetAiMapResponse,
  GetDefectCataloguesResponse,
  GetDefectCatalogueV2Response,
  ListCatalogueVersionsResponse,
  PublishVersionBody,
  PutAiMapBody,
  PutAxisLevelsBody,
  PutValidityBody,
  UpdateCatalogueBody,
  UpdateEntryBody,
} from "@aeriosiq/types";

const STALE_TIME_MS = 5 * 60_000;
const GC_TIME_MS = 10 * 60_000;

/** Per-request axios config that re-points the base at the taxonomy gateway. */
function tx(): { baseURL: string } {
  return { baseURL: getTaxonomyBaseUrl() };
}

const enc = encodeURIComponent;

/**
 * Tenant-readiness gate — MUST be part of `enabled` on every read below.
 *
 * The shared axios instance attaches `X-Tenant-ID` from
 * `authStore.tenant?.tenantSlug`, and that tenant is resolved **async**
 * (`tenant-from-host`). A query that fires on mount before it lands sends NO
 * header, and the gateway's `resolveTenantStrict` rejects with **403** (its
 * missing-slug branch — not a 401, so the refresh-on-401 interceptor never
 * retries it either). With `retry: 1` both attempts lose the same race, so the
 * page sticks on "Catalogues unavailable" until some unrelated refetch happens
 * to win — the exact symptom seen on 2026-07-27 (12 × `403 no-slug` on
 * `GET /api/lyse/v1/defect-catalogues` in `.gateway.log`).
 *
 * Same class of bug as the dashboard-layout 400 fixed in #270; the
 * `useExpandedMe` / `useSessions` gate is the canonical shape.
 */
function useTenantReady(): boolean {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  return (
    !!session?.accessToken &&
    !!tenant?.tenantSlug &&
    session.tenant.tenantSlug === tenant.tenantSlug
  );
}

export const taxonomyKeys = {
  all: ["taxonomy"] as const,
  catalogues: ["taxonomy", "catalogues"] as const,
  versions: (catalogueId: string) =>
    ["taxonomy", "catalogues", catalogueId, "versions"] as const,
  version: (catalogueId: string, versionId: string) =>
    ["taxonomy", "catalogues", catalogueId, "versions", versionId] as const,
  diff: (catalogueId: string, versionId: string, against: string | null) =>
    [
      "taxonomy",
      "catalogues",
      catalogueId,
      "versions",
      versionId,
      "diff",
      against ?? "current",
    ] as const,
  aiMap: (catalogueId: string, versionId: string) =>
    [
      "taxonomy",
      "catalogues",
      catalogueId,
      "versions",
      versionId,
      "ai-map",
    ] as const,
};

// ── Catalogue LIST (reuses lyse GET /defect-catalogues, default base) ───────

/**
 * Lists the tenant's catalogues for the admin list page. Reuses the
 * `lyse/v1@7.9.0` `GET /defect-catalogues` read endpoint (taxonomy/v1 owns
 * only the write surface). Default base — no `tx()` override.
 */
export function useAdminCataloguesQuery() {
  const api = useApi();
  const tenantReady = useTenantReady();
  return useQuery<GetDefectCataloguesResponse>({
    queryKey: taxonomyKeys.catalogues,
    enabled: tenantReady,
    queryFn: async () => {
      const res =
        await api.get<GetDefectCataloguesResponse>("/defect-catalogues");
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Catalogues unavailable" },
  });
}

// ── Version + structure reads (taxonomy base) ───────────────────────────────

/** GET /defect-catalogues/{id}/versions. */
export function useCatalogueVersionsQuery(catalogueId: string | null) {
  const api = useApi();
  const tenantReady = useTenantReady();
  return useQuery<ListCatalogueVersionsResponse>({
    queryKey: taxonomyKeys.versions(catalogueId ?? ""),
    enabled: tenantReady && catalogueId !== null && catalogueId.length > 0,
    queryFn: async () => {
      const id = catalogueId ?? "";
      const res = await api.get<ListCatalogueVersionsResponse>(
        `/defect-catalogues/${enc(id)}/versions`,
        tx(),
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Versions unavailable" },
  });
}

/** GET /defect-catalogues/{id}/versions/{versionId} — full two-axis structure. */
export function useCatalogueVersionQuery(
  catalogueId: string | null,
  versionId: string | null,
) {
  const api = useApi();
  const tenantReady = useTenantReady();
  return useQuery<GetDefectCatalogueV2Response>({
    queryKey: taxonomyKeys.version(catalogueId ?? "", versionId ?? ""),
    enabled:
      tenantReady &&
      catalogueId !== null &&
      catalogueId.length > 0 &&
      versionId !== null &&
      versionId.length > 0,
    queryFn: async () => {
      const cid = catalogueId ?? "";
      const vid = versionId ?? "";
      const res = await api.get<GetDefectCatalogueV2Response>(
        `/defect-catalogues/${enc(cid)}/versions/${enc(vid)}`,
        tx(),
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Catalogue version unavailable" },
  });
}

/** GET /defect-catalogues/{id}/versions/{versionId}/diff?against=. */
export function useCatalogueDiffQuery(
  catalogueId: string | null,
  versionId: string | null,
  against: string | null,
  enabled = true,
) {
  const api = useApi();
  const tenantReady = useTenantReady();
  return useQuery<CatalogueDiff>({
    queryKey: taxonomyKeys.diff(
      catalogueId ?? "",
      versionId ?? "",
      against ?? null,
    ),
    enabled:
      enabled &&
      tenantReady &&
      catalogueId !== null &&
      catalogueId.length > 0 &&
      versionId !== null &&
      versionId.length > 0,
    queryFn: async () => {
      const cid = catalogueId ?? "";
      const vid = versionId ?? "";
      const q =
        against !== null && against.length > 0
          ? `?against=${enc(against)}`
          : "";
      const res = await api.get<CatalogueDiff>(
        `/defect-catalogues/${enc(cid)}/versions/${enc(vid)}/diff${q}`,
        tx(),
      );
      return res.data;
    },
    staleTime: 0,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: false,
    meta: { failureToast: "Diff unavailable" },
  });
}

// ── Catalogue lifecycle mutations ───────────────────────────────────────────

export function useCreateCatalogueMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DefectCatalogueAdmin, Error, CreateCatalogueBody>({
    mutationFn: async (body) => {
      const res = await api.post<DefectCatalogueAdmin>(
        "/defect-catalogues",
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxonomyKeys.catalogues });
    },
  });
}

export function useUpdateCatalogueMutation(catalogueId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DefectCatalogueAdmin, Error, UpdateCatalogueBody>({
    mutationFn: async (body) => {
      const res = await api.patch<DefectCatalogueAdmin>(
        `/defect-catalogues/${enc(catalogueId)}`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxonomyKeys.catalogues });
    },
  });
}

export function useDeleteCatalogueMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (catalogueId: string): Promise<void> => {
      await api.delete(`/defect-catalogues/${enc(catalogueId)}`, tx());
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taxonomyKeys.catalogues });
    },
  });
}

// ── Version lifecycle mutations ─────────────────────────────────────────────

/** POST .../versions — branch a new DRAFT from the current published version. */
export function useBranchVersionMutation(catalogueId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<CatalogueVersionSummary>({
    mutationFn: async () => {
      const res = await api.post<CatalogueVersionSummary>(
        `/defect-catalogues/${enc(catalogueId)}/versions`,
        undefined,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: taxonomyKeys.versions(catalogueId),
      });
      void qc.invalidateQueries({ queryKey: taxonomyKeys.catalogues });
    },
  });
}

/** POST .../publish — freeze the DRAFT + set current. */
export function usePublishVersionMutation(
  catalogueId: string,
  versionId: string,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<CatalogueVersionSummary, Error, PublishVersionBody>({
    mutationFn: async (body) => {
      const res = await api.post<CatalogueVersionSummary>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/publish`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: taxonomyKeys.versions(catalogueId),
      });
      void qc.invalidateQueries({ queryKey: taxonomyKeys.catalogues });
    },
  });
}

// ── Entry CRUD (draft only) ─────────────────────────────────────────────────

export function useCreateEntryMutation(catalogueId: string, versionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DefectCatalogueEntryV2, Error, CreateEntryBody>({
    mutationFn: async (body) => {
      const res = await api.post<DefectCatalogueEntryV2>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/entries`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: taxonomyKeys.version(catalogueId, versionId),
      });
    },
  });
}

export function useUpdateEntryMutation(catalogueId: string, versionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    DefectCatalogueEntryV2,
    Error,
    { entryId: string; body: UpdateEntryBody }
  >({
    mutationFn: async ({ entryId, body }) => {
      const res = await api.patch<DefectCatalogueEntryV2>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/entries/${enc(entryId)}`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: taxonomyKeys.version(catalogueId, versionId),
      });
    },
  });
}

/**
 * Batch sibling reorder with a SYNCHRONOUS optimistic cache update
 * (drag-and-drop).
 *
 * Two failure modes this kills (Christian, 2026-06-04):
 *  1. Refetch-only: every row "jumps back, then jumps to place" after drop.
 *  2. onMutate-async: the DRAGGED row is fine, but the displaced SIBLINGS
 *     still flash — onMutate runs a few microtasks after the drop (it
 *     awaits cancelQueries first), so dnd-kit clears their shift
 *     transforms against the OLD cached order, and the optimistic write
 *     lands a paint later.
 *
 * Therefore `reorderNow()` writes the cache SYNCHRONOUSLY in the caller's
 * event tick — React batches the cache-driven re-sort into the SAME commit
 * as dnd-kit's drag-end state, so nothing moves twice. The snapshot for
 * rollback travels with the mutation variables; onError restores it;
 * onSettled re-syncs with the server.
 */
export function useReorderEntriesMutation(
  catalogueId: string,
  versionId: string,
) {
  const api = useApi();
  const qc = useQueryClient();
  const key = taxonomyKeys.version(catalogueId, versionId);
  const mutation = useMutation<
    null,
    Error,
    {
      patches: { entryId: string; sortOrder: number }[];
      prev: GetDefectCatalogueV2Response | undefined;
    }
  >({
    mutationFn: async ({ patches }) => {
      await Promise.all(
        patches.map((p) =>
          api.patch<DefectCatalogueEntryV2>(
            `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/entries/${enc(p.entryId)}`,
            { sortOrder: p.sortOrder } satisfies UpdateEntryBody,
            tx(),
          ),
        ),
      );
      return null;
    },
    onMutate: ({ patches: _patches }) => {
      // The optimistic write already happened synchronously in reorderNow().
      // Stop in-flight refetches so they can't clobber it mid-mutation.
      void qc.cancelQueries({ queryKey: key });
    },
    onError: (_err, vars) => {
      if (vars.prev) qc.setQueryData(key, vars.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  /** Apply the new order to the cache NOW (same event tick / same React
   *  commit as the drop), then persist. */
  function reorderNow(patches: { entryId: string; sortOrder: number }[]): void {
    if (patches.length === 0) return;
    const prev = qc.getQueryData<GetDefectCatalogueV2Response>(key);
    if (prev) {
      const bySortOrder = new Map(patches.map((p) => [p.entryId, p.sortOrder]));
      qc.setQueryData<GetDefectCatalogueV2Response>(key, {
        ...prev,
        entries: prev.entries.map((e) => {
          const so = bySortOrder.get(e.entryId);
          return so === undefined ? e : { ...e, sortOrder: so };
        }),
      });
    }
    mutation.mutate({ patches, prev });
  }

  return { ...mutation, reorderNow };
}

export function useDeleteEntryMutation(catalogueId: string, versionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string): Promise<void> => {
      await api.delete(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/entries/${enc(entryId)}`,
        tx(),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: taxonomyKeys.version(catalogueId, versionId),
      });
    },
  });
}

// ── Overlay + structure PUTs (draft only) ───────────────────────────────────

export function usePutValidityMutation(catalogueId: string, versionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<GetDefectCatalogueV2Response, Error, PutValidityBody>({
    mutationFn: async (body) => {
      const res = await api.put<GetDefectCatalogueV2Response>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/validity`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(taxonomyKeys.version(catalogueId, versionId), data);
    },
  });
}

export function usePutAxisLevelsMutation(
  catalogueId: string,
  versionId: string,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<GetDefectCatalogueV2Response, Error, PutAxisLevelsBody>({
    mutationFn: async (body) => {
      const res = await api.put<GetDefectCatalogueV2Response>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/axis-levels`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(taxonomyKeys.version(catalogueId, versionId), data);
    },
  });
}

/**
 * AIF-05 — GET .../ai-map. Read the version-scoped AI map (draft AND published).
 * Each row carries the joined display fields (`aiClassName` snapshot, `modelId`,
 * `classIndex`, `displayName`). Fixes the baseline read-back gap.
 */
export function useAiMapQuery(
  catalogueId: string | null,
  versionId: string | null,
) {
  const api = useApi();
  const tenantReady = useTenantReady();
  return useQuery<GetAiMapResponse>({
    queryKey: taxonomyKeys.aiMap(catalogueId ?? "", versionId ?? ""),
    enabled:
      tenantReady &&
      catalogueId !== null &&
      catalogueId.length > 0 &&
      versionId !== null &&
      versionId.length > 0,
    queryFn: async () => {
      const cid = catalogueId ?? "";
      const vid = versionId ?? "";
      const res = await api.get<GetAiMapResponse>(
        `/defect-catalogues/${enc(cid)}/versions/${enc(vid)}/ai-map`,
        tx(),
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "AI map unavailable" },
  });
}

/**
 * AIF-05 — PUT .../ai-map. Whole-map replace (draft-only). The contract returns
 * the catalogue structure (`GetDefectCatalogueResponse`) on 200; the AIF-05 422
 * `AiMapValidationError` carries per-row issues — the tab reads them off the
 * axios error response. On success we refetch the ai-map read-back so the table
 * re-renders from server-derived display fields.
 */
export function usePutAiMapMutation(catalogueId: string, versionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<GetDefectCatalogueV2Response, Error, PutAiMapBody>({
    mutationFn: async (body) => {
      const res = await api.put<GetDefectCatalogueV2Response>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/ai-map`,
        body,
        tx(),
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(taxonomyKeys.version(catalogueId, versionId), data);
      void qc.invalidateQueries({
        queryKey: taxonomyKeys.aiMap(catalogueId, versionId),
      });
    },
  });
}

/**
 * DL-04 Part A — `GET …/versions/{versionId}/export` (taxonomy/v1 2.4.0
 * `exportCatalogueVersion`, platform admin only). A one-shot download, not a
 * cached query: the file is saved as served.
 */
export function useExportCatalogueVersionMutation(catalogueId: string) {
  const api = useApi();
  return useMutation<CatalogueExport, unknown, string>({
    mutationFn: async (versionId) => {
      const res = await api.get<CatalogueExport>(
        `/defect-catalogues/${enc(catalogueId)}/versions/${enc(versionId)}/export`,
        tx(),
      );
      return res.data;
    },
    meta: { failureToast: "The catalogue could not be exported." },
  });
}
